// Result rendering: verdict banner + honest profile notice + findings list + (optional) the
// official invoice preview. All copy is plain, friendly German (§3.2); no tax advice (§3.4).
// KICKOFF SCOPE: this is a rudimentary but real view. Visual polish, the findings table layout,
// plain-German rule explanations (Phase 3) and the styled invoice preview are the Sonnet task.

import type { Detection, Finding, ProfileClass, ValidationResult } from '../core/types';

export function renderBusy(container: HTMLElement, filename?: string): void {
  container.replaceChildren();
  const p = el('p', 'status status--busy');
  p.textContent = filename ? `Prüfe „${filename}“ …` : 'Prüfe Rechnung …';
  container.append(p);
}

export function renderResult(container: HTMLElement, result: ValidationResult): void {
  container.replaceChildren();

  if (result.error) {
    container.append(banner('error', 'Datei konnte nicht gelesen werden', result.error));
    return;
  }

  container.append(verdictBanner(result));

  if (!result.detection.refereeable) {
    container.append(profileNotice(result.detection));
  }

  if (result.findings.length) {
    container.append(findingsSection(result.findings));
  }

  if (result.renderedHtml) {
    container.append(invoicePreview(result.renderedHtml));
  }

  container.append(detectionFootnote(result.detection));
}

// --- verdict banner ---
function verdictBanner(result: ValidationResult): HTMLElement {
  const nonInfo = result.findings.filter((f) => f.severity !== 'information').length;
  if (result.verdict === 'valid') {
    return banner('ok', '✓ Gültig', 'Diese Rechnung erfüllt alle geprüften Regeln (XRechnung 3.0.x).');
  }
  if (result.verdict === 'invalid') {
    const n = nonInfo || result.findings.length;
    return banner(
      'error',
      `✗ ${n} ${n === 1 ? 'Problem' : 'Probleme'} gefunden`,
      'Diese Rechnung entspricht nicht in allen Punkten den Regeln für XRechnung 3.0.x.',
    );
  }
  return banner(
    'neutral',
    'Kein verbindliches Urteil',
    'Diese Datei kann mit dieser Version nicht abschließend geprüft werden — Details unten.',
  );
}

// --- honest "eingeschränkte Prüfung" notice (D-010 / D-022) ---
const NOTICE: Record<ProfileClass, { title: string; body: string }> = {
  'xrechnung-current': { title: '', body: '' },
  'xrechnung-older': {
    title: 'Andere XRechnung-Version',
    body: 'Diese Datei ist eine XRechnung, aber nicht in der Version, die Rechnungslupe derzeit prüft (3.0). Die Rechnung wird angezeigt, ein verbindliches Prüfurteil geben wir dafür nicht ab.',
  },
  'en16931-other': {
    title: 'Eingeschränkte Prüfung für dieses Profil',
    body: 'Diese Rechnung folgt einem anderen EN-16931-Profil als XRechnung. Sie wird angezeigt, ein verbindliches Prüfurteil geben wir nur für XRechnung ab.',
  },
  'non-en16931': {
    title: 'Eingeschränkte Prüfung für dieses Profil',
    body: 'Dieses ZUGFeRD/Factur-X-Profil ist nicht als XRechnung ausgewiesen. Wir zeigen an, was wir können — ein verbindliches Prüfurteil geben wir dafür nicht ab.',
  },
  unknown: {
    title: 'Profil nicht erkannt',
    body: 'Das Profil dieser Datei konnte nicht eindeutig erkannt werden. Wir zeigen an, was wir können, geben aber kein verbindliches Prüfurteil ab.',
  },
};

function profileNotice(detection: Detection): HTMLElement {
  const n = NOTICE[detection.profileClass];
  const box = el('aside', 'notice');
  box.setAttribute('role', 'note');
  const h = el('h2', 'notice__title');
  h.textContent = n.title || 'Hinweis';
  const p = el('p', 'notice__body');
  p.textContent = n.body;
  box.append(h, p);
  if (detection.profileId) {
    const code = el('p', 'notice__profile');
    code.textContent = `Erkanntes Profil: ${detection.profileId}`;
    box.append(code);
  }
  return box;
}

// --- findings ---
const SEV_LABEL: Record<Finding['severity'], string> = {
  fatal: 'Fehler',
  error: 'Fehler',
  warning: 'Warnung',
  information: 'Hinweis',
};
const SEV_ORDER: Record<Finding['severity'], number> = { fatal: 0, error: 1, warning: 2, information: 3 };

function findingsSection(findings: Finding[]): HTMLElement {
  const section = el('section', 'findings');
  section.setAttribute('aria-label', 'Gefundene Probleme');
  const h = el('h2', 'findings__title');
  h.textContent = 'Details';
  const ul = el('ul', 'findings__list');
  for (const f of [...findings].sort((a, b) => SEV_ORDER[a.severity] - SEV_ORDER[b.severity])) {
    const li = el('li', `finding finding--${f.severity}`);
    const head = el('div', 'finding__head');
    const chip = el('span', 'finding__sev');
    chip.textContent = SEV_LABEL[f.severity];
    const id = el('span', 'finding__id');
    id.textContent = f.ruleId;
    head.append(chip, id);

    // Klartext-Erklärung zuerst und prominent (Abnahme-Anforderung: verständlich ohne Fachwissen).
    const msg = el('p', 'finding__msg');
    msg.textContent = f.plainDe || f.officialMessage;

    li.append(head, msg);

    // Amtlicher Wortlaut bleibt unverändert erhalten, aber eingeklappt — für Rückfragen an
    // Softwarehersteller/IT, nie als Ersatz für die Klartext-Erklärung (§3.4).
    if (f.plainDe && f.officialMessage && f.plainDe !== f.officialMessage) {
      const details = document.createElement('details');
      details.className = 'finding__official';
      const summary = document.createElement('summary');
      summary.textContent = 'Amtlichen Wortlaut anzeigen';
      const officialP = el('p', 'finding__official-text');
      officialP.textContent = f.officialMessage;
      details.append(summary, officialP);
      li.append(details);
    }

    ul.append(li);
  }
  section.append(h, ul);
  return section;
}

// --- official invoice preview (sandboxed) ---
function invoicePreview(html: string): HTMLElement {
  const section = el('section', 'preview');
  section.setAttribute('aria-label', 'Darstellung der Rechnung');
  const h = el('h2', 'preview__title');
  h.textContent = 'Rechnung';
  const frame = document.createElement('iframe');
  frame.className = 'preview__frame';
  // "allow-scripts" WITHOUT "allow-same-origin" — the iframe still gets a unique opaque origin,
  // isolated from the parent page, no cookies/storage access, unable to navigate the top frame.
  // Needed for the official viewer's own tab-switching JS (xrechnung-viewer.js) — without it every
  // preview shows the stylesheet's own "eingeschränkt ohne JavaScript" fallback notice and the
  // Details/Zusätze/Anlagen/Laufzettel tabs don't switch. Audited: xrechnung-viewer.js does pure
  // DOM manipulation + local Blob-based attachment downloads (via FileSaver, vendored
  // FileSaver-v2.0.5.js) — no fetch/XMLHttpRequest/navigation reachable from how the stylesheet
  // calls it (saveAs() is only ever invoked with a real Blob, never a URL string, so FileSaver's
  // XHR fallback path is dead code here). The zero-network CI gate (§3.1/§6.3) exercises this
  // exact preview with render:true and continuously re-verifies no network request occurs (D-036).
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.setAttribute('title', 'Dargestellte Rechnung');
  frame.srcdoc = html;
  section.append(h, frame);
  return section;
}

function detectionFootnote(detection: Detection): HTMLElement {
  const p = el('p', 'footnote');
  const kind =
    detection.container === 'pdf'
      ? `PDF-Rechnung (ZUGFeRD/Factur-X${detection.attachmentName ? `, Anhang „${detection.attachmentName}“` : ''})`
      : 'XML-Rechnung';
  const syntaxLabel = detection.syntax === 'CII' ? 'UN/CEFACT-Format' : 'UBL-Format';
  p.textContent = `Erkannt als: ${kind}, ${syntaxLabel}.`;
  return p;
}

// --- tiny DOM helpers ---
function el(tag: string, className: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = className;
  return e;
}
function banner(kind: 'ok' | 'error' | 'neutral', title: string, body: string): HTMLElement {
  const box = el('div', `banner banner--${kind}`);
  box.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  const h = el('h2', 'banner__title');
  h.textContent = title;
  const p = el('p', 'banner__body');
  p.textContent = body;
  box.append(h, p);
  return box;
}
